import { useLayoutEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { ToastContainer } from 'react-toastify';
import { Route, Redirect, Switch } from 'react-router-dom';
import { GlobalProvider, useGlobalContext, signUpEnabled } from './context/GlobalContext';
import { AppBarProvider } from './context/AppBarContext';
import Home from './pages/Home';
import Main from './pages/Main';
import Team from './pages/Team';
import View from './pages/View/';
import PrintService from './pages/PrintService';
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import SignOut from './components/auth/SignOut';
import ResetPassword from './components/auth/ResetPassword';
import NewPassword from './components/auth/ResetPassword/components/NewPassword';
import NavBar from './components/NavBar';
import Settings from './pages/Settings';
import Contact from './pages/Contact';
import SongForm from './components/SongForm';
import ServiceForm from './pages/ServiceForm';
import NotFound from './components/NotFound';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicSongs from './pages/PublicSongs';
import Subscribe from './pages/Subscribe';
import WelcomeModal from './components/modals/WelcomeModal';
import WhatsNewModal from './components/modals/WhatsNewModal';
import GlobalLoader from './components/common/GlobalLoader/GlobalLoader';
import PullToRefresh from './components/common/PullToRefresh';
import theme from './utils/theme';
import { SUBSCRIPTION_ENABLED } from './utils/subscription';
import { applyColorTheme } from './utils/colorTheme';
import { applyScrollbarStyle } from './utils/scrollbars';
import { getSetting, settingKeys } from './utils/settings';
import { loadWelcomeModalData, clearWelcomeModalData } from './helpers/welcomeModal';
import { cleanupOldCooldowns } from './helpers/songStats';
import { loadReleaseNotes, markVersionSeen } from './helpers/releaseNotes';
import { syncAccountLanguage } from './helpers/accountLanguage';
import auth from './services/authService';

import 'react-toastify/dist/ReactToastify.css';
import './App.scss';

if (process.env.NODE_ENV !== 'development')
  Sentry.init({
    dsn: 'https://5dbb126a850544cb8b45949eda9150e8@o418916.ingest.sentry.io/5325307',
  });

function AppContent() {
  const {
    appLoading,
    connectionError,
    showRemovedFromTeamModal,
    showWelcomeModal,
    setShowWelcomeModal,
    welcomeModalData,
    setWelcomeModalData,
  } = useGlobalContext();
  const [release, setRelease] = useState(null);

  useLayoutEffect(() => {
    theme.applyTheme();
    applyColorTheme();
    applyScrollbarStyle();
    loadWelcomeModalData(setWelcomeModalData, setShowWelcomeModal);

    // Signed out, nothing is stamped, so the notes wait for sign-in. A first visit has the welcome
    // modal instead, and starts from this version.
    const user = auth.getCurrentUser();

    if (user?.teamId) {
      if (getSetting(settingKeys.welcomeModalData)) markVersionSeen();
      else setRelease(loadReleaseNotes(!!user.isAdmin));
    }
    // Signed in, the account's language wins over the copy this device started in
    syncAccountLanguage(user);
    // Play cooldowns expire after 24h but nothing was clearing the expired ones out
    cleanupOldCooldowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    clearWelcomeModalData();
  };

  const handleCloseWhatsNew = () => {
    setRelease(null);
    markVersionSeen();
  };

  return (
    <>
      <GlobalLoader show={appLoading} error={connectionError} />
      <ToastContainer />
      <PullToRefresh />
      {!showRemovedFromTeamModal && <NavBar />}
      <Switch>
        <ProtectedRoute path="/print/service/:id" component={PrintService} />
        <ProtectedRoute path="/view/:service/:id" component={View} />
        <ProtectedRoute path="/view/:id" component={View} />
        <ProtectedRoute path="/join-team" component={SignUp} />
        <Route path="/signup/:teamId" component={SignUp} />
        <Route path="/signup" component={SignUp} />
        <Route path="/signin" component={SignIn} />
        <Route path="/signout" component={SignOut} />
        <Route path="/resetpassword/:userId/:token" component={NewPassword} />
        <Route path="/resetpassword" component={ResetPassword} />
        <ProtectedRoute path="/services/:id" component={ServiceForm} />
        {SUBSCRIPTION_ENABLED && <ProtectedRoute path="/subscribe" component={Subscribe} />}
        {/* Only an admin can save a song, so a member is sent to the song itself */}
        <ProtectedRoute
          path="/songs/:id"
          component={SongForm}
          adminRedirect={({ id }) => (id === 'new' ? '/songs' : `/view/${id}`)}
        />
        <ProtectedRoute path="/songs" component={Main} />
        <ProtectedRoute path="/myteam" component={Team} />
        <ProtectedRoute path="/settings" component={Settings} />
        <ProtectedRoute path="/contact" component={Contact} />
        <ProtectedRoute path="/searchsongs" component={PublicSongs} />
        <Route path="/not-found" component={NotFound} />
        <Route path="/" exact component={signUpEnabled ? Home : SignIn} />
        {/* <Redirect from="/" exact to="/songs" /> */}
        <Redirect to="/not-found" />
      </Switch>

      <WelcomeModal isOpen={showWelcomeModal} onClose={handleCloseWelcomeModal} welcomeModalData={welcomeModalData} />
      <WhatsNewModal isOpen={!!release} onClose={handleCloseWhatsNew} release={release} />
    </>
  );
}

function App() {
  return (
    <GlobalProvider>
      <AppBarProvider>
        <AppContent />
      </AppBarProvider>
    </GlobalProvider>
  );
}

export default App;
